import { useEffect, useState } from 'react';
import { api, type Carteira as DadosCarteira, type CarteiraCartela, type Sessao } from '../api';
import { useRota } from '../rota';
import { diaDoCalendario, emMoedas } from '../formato';
import { LogoOrbita, Moeda, OrbitaDeFundo } from '../orbita';
import { Foto } from '../componentes/Foto';
import { CartaoDeCredito } from './Credito';
import { CartelaDesenhada } from '../../../compartilhado/CartelaDesenhada';
import { tintaSobre } from '../../../compartilhado/cor';
import { Carregando } from '../../../compartilhado/Espera';

const brl = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * A tela 1 do mockup: "quanto eu tenho e o que falta?". O número gigante, o
 * "faltam R$ X" que é o coração do programa (§12.1 — o app mostra o vão EM
 * REAIS, nunca em porcentagem), a próxima recompensa com a barra, o botão do
 * código e a prateleira de prêmios.
 */
export function Carteira({
  sessao,
  aoFalhar,
  abrirCodigo,
}: {
  sessao: Sessao;
  aoFalhar: (e: unknown) => void;
  abrirCodigo: () => void;
}) {
  const { ir } = useRota();
  const [dados, setDados] = useState<DadosCarteira | CarteiraCartela | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api
      .carteira()
      .then(setDados)
      .catch((e) => {
        setErro('não consegui carregar sua carteira');
        aoFalhar(e);
      });
  }, [aoFalhar]);

  if (erro) return <div className="erro-tela">{erro}</div>;
  /* Primeira abertura: não há nada para cobrir, então a esfera fica sozinha no
     vão. Nada aqui recarrega — a carteira é buscada uma vez e pronto. */
  if (!dados)
    return (
      <div className="espera-tela">
        <Carregando />
      </div>
    );

  /*
   * ⚠️ MODO CARTELA: outra tela inteira, e não um pedaço a mais nesta.
   *
   * Aqui a pergunta "o que eu tenho?" se responde em VISITAS. Nada do que vem
   * abaixo (saldo, o vão em reais, a próxima recompensa, a prateleira) existe
   * nesse modo — mostrar meia tela de moeda ao lado dos carimbos faria a
   * cliente procurar um saldo que nunca vai andar.
   */
  if ('cartela' in dados) {
    return <TelaCartela dados={dados} sessao={sessao} abrirCodigo={abrirCodigo} />;
  }

  const plural = dados.saldoMoedas === 1 ? dados.nomeDaMoeda : `${dados.nomeDaMoeda}s`;
  const pct = dados.proxima
    ? Math.min(100, Math.round((dados.saldoMoedas / dados.proxima.custoMoedas) * 100))
    : 100;

  return (
    <div className="tela ca">
      <OrbitaDeFundo />
      <header className="ca-topo">
        <div className="ca-loja">
          <LogoOrbita />
          <span>{sessao.nomeLoja}</span>
        </div>
        <div className="ca-avatar">{(sessao.nome ?? sessao.telefone).slice(0, 2).toLowerCase()}</div>
      </header>

      <button className="ca-saldo" onClick={() => ir('/extrato')}>
        <div className="ca-numero">{dados.saldoMoedas}</div>
        <div className="ca-moedas">{plural}</div>
        {/* A carência da loja (F1): a moeda já é dela, só ainda não troca. O
            extrato diz o dia de cada uma. */}
        {dados.emCarencia > 0 && (
          <div className="ca-moedas">
            {dados.emCarencia} {dados.emCarencia === 1 ? 'chega' : 'chegam'} em breve
          </div>
        )}
      </button>

      {/* Só com a validade ligada na loja: o servidor nem manda o campo sem ela. */}
      {dados.vencimento && (
        <div className="ca-vence">
          {dados.vencimento.renova
            ? `seu saldo vence em ${diaDoCalendario(dados.vencimento.dia)} — cada compra renova`
            : `${emMoedas(dados.vencimento.moedas, dados.nomeDaMoeda)} ${
                dados.vencimento.moedas === 1 ? 'vence' : 'vencem'
              } em ${diaDoCalendario(dados.vencimento.dia)}`}
        </div>
      )}

      <div className="ca-falta">
        <div className="ca-falta-numero">
          faltam <span>{brl(dados.faltaCentavos)}</span>
        </div>
        <div className="ca-falta-sub">para a sua próxima {dados.nomeDaMoeda}</div>
      </div>

      {/* F9: o crédito, quando a loja usa. Busca o próprio dado e some
          quando não há — a carteira não precisa saber que ele existe. */}
      <CartaoDeCredito ir={ir} />

      {dados.proxima && (
        <button className="ca-proxima" onClick={() => ir(`/recompensa/${dados.proxima!.id}`)}>
          <Foto capa={dados.proxima.capa} nome={dados.proxima.nome} className="ca-foto pequena" />
          <div className="ca-proxima-meio">
            <div className="ca-rotulo">próxima recompensa</div>
            <div className="ca-proxima-nome">{dados.proxima.nome}</div>
            {dados.proxima.faltam !== undefined && dados.proxima.faltam > 0 && (
              <div className="ca-proxima-falta">
                {dados.proxima.faltam === 1 ? 'falta' : 'faltam'}{' '}
                {emMoedas(dados.proxima.faltam, dados.nomeDaMoeda)}
              </div>
            )}
            <div className="ca-barra">
              <div style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="ca-custo">
            <Moeda />
            <span>{dados.proxima.custoMoedas}</span>
          </div>
        </button>
      )}

      <button className="botao-cheio ca-codigo" onClick={abrirCodigo}>
        Meu código
      </button>

      <div className="ca-prateleira">
        {dados.destaques.map((d) => (
          <button key={d.id} className="ca-cartinha" onClick={() => ir(`/recompensa/${d.id}`)}>
            <Foto capa={d.capa} nome={d.nome} className="ca-foto media" />
            <div className="ca-cartinha-nome">{d.nome}</div>
            <div className="ca-cartinha-custo">
              <Moeda tamanho={6} />
              <span>
                {d.custoMoedas} {d.custoMoedas === 1 ? dados.nomeDaMoeda : `${dados.nomeDaMoeda}s`}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


/**
 * A cartela de carimbos, na tela da cliente.
 *
 * Segue o canvas aprovado (`docs/fidelidade/design/Cerchia Cartela.html`,
 * artboards A1–A3): cabeçalho da loja com a pílula do código, a manchete, e o
 * cartão da cartela com a grade dentro e o prêmio no rodapé pontilhado.
 *
 * ⚠️ **A pergunta que a tela responde em três segundos é "quantas faltam?"**
 * — manchete, nunca porcentagem. É a mesma regra do "faltam R$ 10" do modo
 * moeda (§12.1): porcentagem é abstração, visita é coisa que a pessoa sabe
 * fazer.
 *
 * ⚠️ **A cor é da LOJA, não da Cerchia.** Chega em `dados.cor` e entra como
 * `--accent` no escopo da tela — carimbo, avatar e a pílula do código nascem
 * na marca dela. Carimbo dado não é verde nem "sucesso": é progresso, e
 * progresso aqui é identidade.
 */
function TelaCartela({
  dados,
  sessao,
  abrirCodigo,
}: {
  dados: CarteiraCartela;
  sessao: Sessao;
  abrirCodigo: () => void;
}) {
  const { ir } = useRota();
  const c = dados.cartela;
  const cor = dados.cor || undefined;
  const inicialDaLoja = (sessao.nomeLoja ?? 'C').trim().slice(0, 1).toUpperCase();
  const fechada = !!c && c.faltam === 0;

  return (
    <div className="tela ct" style={cor ? ({ ['--accent' as string]: cor } as never) : undefined}>
      <header className="ct-topo">
        {/* ⚠️ A tinta da inicial acompanha a cor da loja: branco sobre tom
            claro é ilegível, e a lojista pode escolher um pastel. */}
        <div className="ct-loja-avatar" style={{ color: tintaSobre(cor ?? '#d4622a') }}>
          {inicialDaLoja}
        </div>
        <div className="ct-loja">
          <div className="ct-loja-nome">{sessao.nomeLoja}</div>
          <div className="ct-loja-sub">cartela de visitas</div>
        </div>
        {/*
          O código vira PÍLULA no topo, e não botão preto no rodapé: no balcão
          a cliente abre o app já procurando o que mostrar, e o rodapé exigia
          rolar a tela inteira com a atendente esperando.

          Some quando a cartela fecha — ali a ação é outra, e duas ações
          competindo no mesmo momento fazem a errada ser tocada.
        */}
        {!fechada && (
          <button className="ct-codigo" onClick={abrirCodigo}>
            Meu código
          </button>
        )}
      </header>

      {!c ? (
        /* Sem cartela: a loja ligou o modo e ainda não abriu a dela. Não é
           erro, e a tela não finge que é. */
        <>
          <div className="ct-faltam">sua cartela abre na primeira visita</div>
          <div className="ct-sub">é só vir — o caixa carimba</div>
        </>
      ) : (
        <>
          {fechada ? (
            <>
              <div className="ct-faltam">cartela completa</div>
              <div className="ct-sub">
                é seu — {c.alvo} visitas, {c.alvo} carimbos
              </div>
            </>
          ) : (
            <>
              <div className="ct-faltam">{c.comoSeLe}</div>
              <div className="ct-sub">para o seu prêmio</div>
            </>
          )}

          <div className="ct-cartao">
            <div className="ct-faixa">
              <span>sua cartela</span>
              <span>
                {c.carimbos} de {c.alvo}
              </span>
            </div>

            <CartelaDesenhada alvo={c.alvo} carimbos={c.carimbos} tamanho={48} />

            <div className="ct-rodape">
              <div className="ct-rodape-rotulo">na {c.alvo}ª visita</div>
              <div className="ct-rodape-premio">{c.premio}</div>
            </div>
          </div>

          {fechada && (
            <button className="ct-botao" onClick={abrirCodigo}>
              Mostrar no caixa
            </button>
          )}

          <div className="ct-instrucao">
            {fechada
              ? 'o cupom também fica guardado em Cupons'
              : 'mostre seu código no caixa a cada visita'}
          </div>
        </>
      )}

      {/* F9: a loja de cartela também pode dar crédito — são módulos
          separados, e a cliente tem direito de ver os dois. */}
      <CartaoDeCredito ir={ir} />
    </div>
  );
}
